import { Pagination } from "@mui/material";
import {
  AvatarIcon,
  CalendarIcon,
  PersonIcon,
  SewingPinFilledIcon,
} from "@radix-ui/react-icons";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { SearchInput } from "../../components/Widget/SearchInput";
import { Tabs } from "../../components/Widget/Tab";
import { store } from "../../services/service";
import { useMaintenanceStore } from "../../store/maintenanceStore";
import { useStoreApp } from "../../store/storeApp";
import { useStoreCommune } from "../../store/storeCommune";
import { useStoreSurvey } from "../../store/SurveyStore";
import { useTabStore } from "../../store/tabStore";
import {
  ENREGISTRER_MAINTENANCE,
  EXPORT_EXCEL_MAINTENANCE,
  LISTE_COMMUNE_SANS_PAGINATION,
  LISTE_MAINTENANCE,
  PRESTATAIRE,
  PRIORITE,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";
import AttenteAttributionEquipe from "./AttenteAttributionEquipe";
import AttenteMaintenance from "./AttenteMaintenance";
import EquipeMaintenance from "./EquipeMaintenance";
import { PrestataireAttributionMaintenance } from "./PrestataireAttributionMaintenance";
import { StatusMaintenanceModal } from "./StatusMaintenanceModal";

export const getPrioriteBadge = (priorite) => {
  const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
  switch (priorite) {
    case PRIORITE.CRITIQUE:
      return `${baseClasses} bg-red-100 text-red-800`;
    case PRIORITE.HAUTE:
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case PRIORITE.MOYENNE:
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case PRIORITE.BASSE:
      return `${baseClasses} bg-green-100 text-green-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800`;
  }
};

const CorbeilleMaintenance = () => {
  const { triggerRefresh } = useMaintenanceStore();
  const { activeTab, setActiveTab } = useTabStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm();

  const dataListe = useStoreSurvey();
  const prestataire = useStoreApp();
  const communeStore = useStoreCommune();
  const tabs = [
    {
      value: "Liste de maintenance",
      index: 0,
      tag: "LISTE_REMONTEES",
    },
    {
      value: "Corbeille de maintenance",
      index: 1,
      tag: "LISTE_REMONTEES_EN_ATTENTE_ATTRIBUTION_PRESTATAIRE",
    },
    {
      value: "Attributions effectuées",
      index: 2,
      tag: "LISTE_REMONTEES_ATTRIBUER_PRESTAIRE",
    },
    {
      value: "Corbeille d'attribution par équipe",
      index: 3,
      tag: "LISTE_REMONTEES_EN_ATTENTE_ATTRIBUTION_EQUIPE",
    },
    {
      value: "Attributions effectuées par équipe",
      index: 4,
      tag: "LISTE_REMONTEES_ATTRIBUER_EQUIPE",
    },
  ];

  const filteredTabsData = tabs
    .filter((tab) => checkTagValidity(tab.tag))
    .map((tab, index) => ({ ...tab, index }));

  useEffect(() => {
    if (filteredTabsData.length !== 0) {
      setActiveTab(filteredTabsData[0]);
    }
    console.log(filteredTabsData);
  }, []);

  // Fonction de recherche avec debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    dataListe
      .getData(`${LISTE_MAINTENANCE}?page=0&param=${value}&size=8&statut=`)
      .finally(() => setIsSearching(false));
  };

  const moreEnAttente = (page) => {
    dataListe.getData(
      `${LISTE_MAINTENANCE}?page=${page - 1}&param=${searchTerm}&size=8&statut=`,
    );
  };

  const submit = async (data) => {
    setLoading(true);
    try {
      await store(ENREGISTRER_MAINTENANCE, "application/json", data).then(
        (res) => {
          if (res.status === 200) {
            setLoading(false);
            document.getElementById("modal-maintenance").close();
            dataListe.getData(
              `${LISTE_MAINTENANCE}?page=0&param=&size=8&statut=`,
            );
            // Refresh the en attente maintenance list
            triggerRefresh();
            reset();
          }
        },
      );
    } catch (err) {
      alert("Erreur lors de l'enregistrement");
    }
  };

  const handleStatusUpdate = () => {
    // Refresh the main maintenance list when status is updated
    dataListe.getData(
      `${LISTE_MAINTENANCE}?page=0&param=${searchTerm}&size=8&statut=`,
    );
  };

  useEffect(() => {
    if (activeTab?.tag === "LISTE_REMONTEES") {
      dataListe.getData(`${LISTE_MAINTENANCE}?page=0&param=&size=8&statut=`);
      prestataire.getAll(PRESTATAIRE);
      communeStore.getData(`${LISTE_COMMUNE_SANS_PAGINATION}`);
    }
  }, []);

  return (
    <div className="">
      {/* Header */}

      <dialog id="modal-maintenance" className="modal">
        <div className="modal-box">
          <form method="dialog" onSubmit={handleSubmit(submit)}>
            <h2 className="font-bold text-lg mb-4">Nouvelle maintenance</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Nom et prenoms du client<span className="text-red-400">*</span>
              </label>
              <input
                {...register("nomPrenomsClient", { required: true })}
                className="input input-bordered w-full"
                placeholder="Nom et prenoms du client"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Contact du client<span className="text-red-400">*</span>
              </label>

              <input
                {...register("contactClient", { required: true })}
                className="input input-bordered w-full"
                placeholder="Contact du client"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Numéro FTTH<span className="text-red-400">*</span>
              </label>
              <input
                {...register("numeroFtth", { required: true })}
                className="input input-bordered w-full"
                placeholder="Numéro FTTH"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Description<span className="text-red-400">*</span>
              </label>
              <textarea
                {...register("description", { required: true })}
                className="textarea textarea-bordered w-full"
                placeholder="Description"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Commune du client<span className="text-red-400">*</span>
              </label>
              <select
                {...register("communeId", { required: true })}
                className="select select-bordered w-full"
                disabled={communeStore.loading}
              >
                <option value="">
                  {communeStore.loading
                    ? "Chargement..."
                    : "Toutes les communes"}
                </option>
                {communeStore?.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.designation}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Priorité</label>
              <select
                {...register("priorite", { required: true })}
                className="select select-bordered w-full"
              >
                <option value="">Sélectionner</option>
                <option value={PRIORITE.CRITIQUE}>Critique</option>
                <option value={PRIORITE.HAUTE}>Haute</option>
                <option value={PRIORITE.MOYENNE}>Moyenne</option>
                <option value={PRIORITE.BASSE}>Basse</option>
              </select>
            </div>
            <div className="modal-action">
              <button
                type="submit"
                className="px-4 py-2.5 bg-main border-main text-gray-800 rounded-lg text-sm"
                disabled={loading || isSubmitting}
              >
                {loading || isSubmitting ? "Enregistrement..." : "Enregistrer"}
              </button>
              <button
                type="button"
                className="px-4 py-2.5 bg-gray-200 text-gray-800 rounded-lg text-sm"
                onClick={() =>
                  document.getElementById("modal-maintenance").close()
                }
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </dialog>

      <div className="lg:flex items-center justify-between mb-4">
        <div>
          <h3 className="font-mtn text-3xl font-bold">
            Gestion des maintenances
          </h3>
          <p className="text-sm text-gray-500">
            Consultez et gérez les demandes de maintenance.
          </p>
        </div>
      </div>
      <div className="flex justify-between items-center gap-2 mb-4">
        {checkTagValidity("REMONTER_MAINTENANCE") && (
          <button
            className="btn btn-sm bg-main border-main flex items-center min-w-[220px] justify-center"
            onClick={async () => {
              document.getElementById("modal-maintenance").showModal();
            }}
          >
            Nouvelle demande de maintenance
          </button>
        )}
        <button
          className="btn btn-sm bg-main border-main flex items-center min-w-[220px] justify-center"
          disabled={exportLoading}
          onClick={async () => {
            setExportLoading(true);
            try {
              const res = await store(EXPORT_EXCEL_MAINTENANCE, null, null, {
                responseType: "blob",
              });
              if (res?.data) {
                const url = window.URL.createObjectURL(res.data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                  "download",
                  "Maintenance_attente_attribution.xlsx",
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
              }
            } catch (err) {
              toast.error("Erreur lors de l'export");
            } finally {
              setExportLoading(false);
            }
          }}
        >
          {exportLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Export en cours...
            </span>
          ) : (
            "Exporter maintenance en attente "
          )}
        </button>
      </div>
      {/* Tabs */}
      <Tabs
        active={activeTab}
        setActive={setActiveTab}
        tabsData={filteredTabsData}
      />

      {/* Contenu principal */}
      {activeTab.tag === "LISTE_REMONTEES" ? (
        <div className="bg-white rounded-md px-2 py-4 mt-3">
          {/* Filtres */}
          <div className="flex items-center gap-3 mb-4">
            <SearchInput
              onChange={(e) => handleSearch(e.target.value)}
              value={searchTerm}
              isSearching={isSearching}
            />

            <select
              onChange={(e) => {
                dataListe.getData(
                  `${LISTE_MAINTENANCE}?page=0&param=&size=8&statut=${e.target.value}`,
                );
              }}
              className="select select-bordered"
            >
              <option value="">Tout selectionner</option>
              <option value="EN_ATTENTE_DE_TRAITEMENT">En attente</option>
              <option value="RESOLUE">Résolue</option>
              <option value="FERMEE">Fermée</option>
            </select>
          </div>

          {/* Grille des rendez-vous */}
          {dataListe?.loading ? (
            <div className="flex flex-col justify-center items-center py-20 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-main" />
              <span className="ml-3 text-gray-600 text-sm">
                Chargement des données...
              </span>
            </div>
          ) : dataListe?.data?.data?.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-sm text-gray-500">Aucune donnée disponible</p>
            </div>
          ) : (
            <>
              <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-2 mt-4 grid-cols-1 gap-5">
                {dataListe?.data?.donnee?.data?.map((item) => (
                  <div
                    key={item.id}
                    className={`relative bg-white border  rounded-xl shadow-sm hover:shadow-md transition-shadow`}
                  >
                    <div className="rounded-t-xl flex items-center gap-3 border-b border-dashed border-amber-400 px-2 py-3">
                      <p className="text-[12px] font-semibold">N° {item?.id}</p>
                      <span className={getPrioriteBadge(item.priorite)}>
                        {item.priorite}
                      </span>
                    </div>
                    <div className="px-3 py-2">
                      <div className="items-center gap-2 space-y-3">
                        <div className="flex gap-1 my-3">
                          <PersonIcon className="bg-gray-100 rounded-full w-8 h-8 p-2" />
                          <div>
                            <p className="text-[13px] font-medium">
                              {item?.nomPrenomsClient}
                            </p>
                            <p className="text-xs flex font-semibold gap-2">
                              <span>{item?.contactClient}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className=" w-fit px-1 py-[2px]">
                            {item?.dateCreation && (
                              <p className="text-xs flex gap-1 items-center font-medium">
                                <CalendarIcon />
                                {new Date(
                                  item?.dateCreation,
                                ).toLocaleDateString("fr-FR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="gap-1">
                          <div>
                            <p className="text-[13px] font-medium">
                              Numero FTTH : {item?.numeroFtth}
                            </p>
                          </div>
                        </div>

                        <h1 className="text-xs flex gap-1 items-center my-2">
                          <AvatarIcon />
                          Rapporteur : {item?.rapporteur?.agent?.nomPrenoms}
                        </h1>
                        {item.description && (
                          <div className="dropdown flex items-center">
                            <p className="text-xs">{item?.description}</p>
                          </div>
                        )}
                      </div>

                      {item?.commune && (
                        <h1 className="my-2 text-[13px] font-medium flex items-center">
                          <SewingPinFilledIcon />
                          {item?.commune?.designation}
                        </h1>
                      )}

                      {item?.raison && (
                        <div className="my-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-xs text-red-700 font-semibold">
                            Raison:
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            {item?.raison}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center gap-1">
                        <p className="bg-blue-50 text-blue-900 py-1 px-1 rounded-lg font-medium text-xs truncate">
                          {item?.statut?.toLowerCase().replace(/_/g, " ")}
                        </p>
                      </div>

                      <div className="flex gap-2 mt-3">
                        <StatusMaintenanceModal
                          item={item}
                          onStatusUpdate={handleStatusUpdate}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="my-3 flex justify-end">
            <Pagination
              onChange={(event, newValue) => moreEnAttente(newValue)}
              count={dataListe?.data?.totalPages}
              variant="outlined"
              color="primary"
              shape="rounded"
            />
          </div>
        </div>
      ) : activeTab?.tag ===
        "LISTE_REMONTEES_EN_ATTENTE_ATTRIBUTION_PRESTATAIRE" ? (
        <AttenteMaintenance />
      ) : activeTab?.tag === "LISTE_REMONTEES_ATTRIBUER_PRESTAIRE" ? (
        <PrestataireAttributionMaintenance />
      ) : activeTab?.tag === "LISTE_REMONTEES_EN_ATTENTE_ATTRIBUTION_EQUIPE" ? (
        <AttenteAttributionEquipe />
      ) : (
        <EquipeMaintenance />
      )}
    </div>
  );
};

export default CorbeilleMaintenance;
